<template>
  <oie-my-pages-template v-element-loader="{endpoint: 'shoppinglist/loadAll'}" :title="t('shoppingListsTitle')" :text="t('shoppingListsSubTitle')">
    <oie-action-wrapper>
      <oie-add-option-slide-in
        :label-text="tStatic('newShoppingListButtonLabel')"
        :title="tStatic('addNewShoppingListHeader')"
        :text="tStatic('addNewShoppingListText')"
        @add-option="createNewShoppingList">
        <template #activator="{open}">
          <oie-base-action
            v-tooltip.top="tStatic('newShoppingListTooltip')"
            theme="flat"
            :only-icon-mobile="true"
            @click="open">
            <template #iconPrepend>
              <oie-base-icon icon="shopping-list" size="s"></oie-base-icon>
            </template>
            {{ tStatic('newShoppingListButtonLabel') }}
          </oie-base-action>
        </template>
      </oie-add-option-slide-in>
    </oie-action-wrapper>
    <oie-action-wrapper
      class="my-shopping-lists__filters"
      :class="{'my-shopping-lists__filters--full-width': !createdByList}">
      <template #left>
        <oie-base-input
          class="my-shopping-lists__filter"
          :value="filter"
          :placeholder="tStatic('searchShoppingListPlaceholder')"
          @input="setFilter">
          <template #iconPrepend>
            <oie-base-icon icon="filter-icon" size="s"></oie-base-icon>
          </template>
        </oie-base-input>
      </template>

      <template v-if="createdByList" #right>
        <oie-base-dropdown
          v-model="createdBy"
          :options="createdByList"
          :is-large="true"
          :use-full-width="true">
        </oie-base-dropdown>
      </template>
    </oie-action-wrapper>

    <div class="my-shopping-lists__content">
      <oie-shopping-lists-table :shopping-lists="createdByFilteredLists"></oie-shopping-lists-table>
    </div>
  </oie-my-pages-template>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import myPages from '@/mixins/my-pages'

export default {
  mixins: [myPages],
  data() {
    return {
      createdBy: null
    }
  },
  computed: {
    ...mapGetters('myShoppingLists', ['shoppingLists', 'createdByList', 'createdByFilter', 'filter', 'filteredShoppingLists']),
    createdByFilteredLists() {
      // If no selection or "All" is selected, return all filtered lists
      if (!this.createdBy || this.createdBy === 'All') {
        return this.filteredShoppingLists
      }

      // Parse the comma-separated list IDs
      const selectedIds = this.createdBy.split(',')

      // Filter shopping lists based on selected IDs
      return this.filteredShoppingLists.filter(list =>
        selectedIds.includes(String(list.id))
      )
    }
  },
  watch: {
    createdBy(newValue) {
      this.setCreatedByFilter(newValue)
      this.setFilter('')
    }
  },
  async mounted() {
    // Save before reset/auto-selection can overwrite it
    const savedFilter = this.createdByFilter

    // Clears createdByList → v-if becomes false → dropdown unmounts, preventing stale auto-selection
    this.resetSelectedShoppingList()
    await this.getShoppingLists()

    // Wait for base-dropdown to mount with fresh options and auto-select options[0]
    await this.$nextTick()

    // Restore the saved filter only if it still matches a current option
    // (option values change when lists are added/removed, making old filters stale)
    if (savedFilter !== null && this.createdByList) {
      const matchingOption = this.createdByList.find(o => String(o.value) === String(savedFilter))
      if (matchingOption) {
        this.createdBy = savedFilter
      }
    }
  },
  methods: {
    ...mapActions('myShoppingLists', ['getShoppingLists', 'resetSelectedShoppingList', 'addNewShoppingList', 'selectShoppingList', 'setFilter', 'setCreatedByFilter']),
    ...mapActions('ui', ['setActiveSlideIn']),
    async createNewShoppingList(name) {
      const response = await this.addNewShoppingList(name)
      this.setActiveSlideIn(null)
      this.setCreatedByFilter(null) // Reset filter so the new list is visible on return
      this.selectShoppingList(response.data.id)
      this.goToSubPage(this.navigationOptions.oieMyShoppingList)
    }
  }
}
</script>

<style lang="scss">
@include media(x-small) {
  .my-shopping-lists__filters.action-wrapper {
    display: flex;
    flex-wrap: nowrap;
    gap: measure(half);
    width: 100%;

    > .action-wrapper__container:first-child {
      flex: 1 1 auto;
      min-width: 0;
    }

    > .action-wrapper__container:last-child {
      flex: 0 0 auto;
    }
  }

  .my-shopping-lists__filters--full-width {
    > .action-wrapper__container:first-child {
      flex: 1 1 100%;
      width: 100%;
    }

    > .action-wrapper__container:last-child {
      display: none;
    }
  }

  .my-shopping-lists__filter {
    width: 100%;
    max-width: none;
  }
}
</style>
